import { useEffect } from "preact/hooks";
import { useQuery } from "@tanstack/react-query";
import { fetchPdpRatingEligibility } from "../lib/api";
import {
  getCustomerIdentifier,
  hasCustomerId,
  type CustomerIdentifier,
} from "../lib/customerIdentity";
import { getShopifyProductId, getShopifyShopId } from "../utils/metadata";
import { trackEvent } from "../lib/tracking";
import { RateProductCard } from "./RateProductCard";

function identifierKey(identifier: CustomerIdentifier | null): string {
  if (!identifier) return "none";
  return identifier.type === "id"
    ? `id:${identifier.shopify_customer_id}`
    : `tok:${identifier.buy_again_token}`;
}

export function RateProductWidget() {
  const shop = getShopifyShopId();
  const productId = getShopifyProductId();
  const identifier = getCustomerIdentifier();
  const enabled =
    shop != null && productId != null && identifier != null && hasCustomerId();

  const { data } = useQuery({
    queryKey: ["pdp-rating-eligibility", shop, productId, identifierKey(identifier)],
    queryFn: () =>
      fetchPdpRatingEligibility({
        shop: shop as string,
        shopifyProductId: productId as number,
        identifier: identifier as CustomerIdentifier,
      }),
    enabled,
    retry: false,
    staleTime: 60_000,
  });

  const eligible = data?.eligible === true ? data : null;

  useEffect(() => {
    if (eligible) trackEvent("product_rating.prompt_shown");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligible?.productId]);

  if (!eligible) return null;

  return (
    <RateProductCard
      shop={shop as string}
      productId={eligible.productId}
      title={eligible.title}
      questionId={eligible.questionId}
      shopifyOrderId={eligible.shopifyOrderId}
    />
  );
}
